
import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import ConceptionCalculator from '@/components/SpecialCalculators/PregnancyCalculators/ConceptionCalculator';

const ConceptionCalculatorPage = () => {
  return (
    <Layout>
      <div className="container mx-auto max-w-3xl px-4">
        <div className="mb-6">
          <Link to="/special" className="flex items-center text-primary hover:underline mb-2">
            <ArrowLeft size={16} className="mr-1" />
            Back to Special Calculators
          </Link>
          <h1 className="text-3xl font-bold mb-2">Conception Calculator</h1>
          <p className="text-gray-600 mb-6">
            Calculate potential conception dates based on your cycle or due date.
          </p>
        </div>
        
        <div className="mb-8">
          <ConceptionCalculator />
        </div>
        
        <div className="mt-10 prose max-w-none">
          <h2 className="text-xl font-semibold mb-4">Understanding Conception</h2>
          <p>
            Conception occurs when a sperm fertilizes an egg, typically around the time of ovulation. 
            This calculator helps you determine when conception most likely occurred or when it might 
            occur in the future.
          </p>
          
          <h3 className="text-lg font-medium mt-6 mb-2">The Conception Process</h3>
          <p>
            For conception to occur, several events must happen in sequence:
          </p>
          <ol className="list-decimal ml-6">
            <li>An egg is released from one of the ovaries (ovulation)</li>
            <li>The egg travels through the fallopian tube toward the uterus</li>
            <li>Sperm must be present in the fallopian tube to fertilize the egg</li>
            <li>Once fertilized, the egg continues to the uterus</li>
            <li>The fertilized egg (zygote) implants in the uterine lining</li>
          </ol>
          
          <h3 className="text-lg font-medium mt-6 mb-2">Timing of Conception</h3>
          <p>
            The most fertile time for conception is during your "fertile window," which includes:
          </p>
          <ul className="list-disc ml-6">
            <li>The day of ovulation</li>
            <li>The 5 days before ovulation</li>
          </ul>
          <p>
            Studies have shown that the likelihood of conception is highest in the two days before 
            ovulation and on the day of ovulation itself.
          </p>
          
          <div className="bg-blue-50 p-4 rounded-md mt-6">
            <h3 className="text-lg font-medium text-blue-800 mb-2">Planning for Conception</h3>
            <p className="text-blue-800">
              If you're trying to conceive, having intercourse every 1-2 days during your fertile 
              window can maximize your chances. Keep in mind that stress, illness, travel, and other 
              factors can affect ovulation timing, so predictions are estimates, not guarantees.
            </p>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-md mt-6">
            <h3 className="text-lg font-medium text-purple-800 mb-2">Calculating in Reverse</h3>
            <p className="text-purple-800">
              If you're already pregnant and using this calculator with your due date, remember that 
              the conception date is an estimate. Due dates themselves are estimates based on averages, 
              so the calculated conception date may differ from the actual date by several days.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ConceptionCalculatorPage;
